import { ForbiddenException, Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { PlanTier, planQuotaBytes, resolvePlanTier } from './plan-quota.constants';

export interface OrgImageUsage {
  usedBytes: number;
  capBytes: number;
  planTier: PlanTier;
  isHardBlocked: boolean;
}

@Injectable()
export class ImageQuotaService {
  constructor(private readonly prisma: PrismaService) {}

  async getUsage(organizationId: string): Promise<OrgImageUsage> {
    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: organizationId },
      select: { plan: true, storageCapBytes: true },
    });

    // Quota is scoped to the Organization, not a single Store — a merchant
    // with multiple stores shares one pool. Deliberately no `isActive`
    // filter here: a trashed-but-not-yet-purged image still occupies real
    // disk bytes until it's hard-deleted, so it must still count. This is
    // the one place this aggregation differs from the per-product "Media
    // Size" column, which only sums live images.
    const agg = await this.prisma.productImage.aggregate({
      where: { store: { organizationId } },
      _sum: { sizeBytes: true },
    });

    const planTier = resolvePlanTier(org.plan);
    const capBytes = org.storageCapBytes != null ? Number(org.storageCapBytes) : planQuotaBytes(org.plan);
    const usedBytes = agg._sum.sizeBytes ?? 0;

    return {
      usedBytes,
      capBytes,
      planTier,
      // Every plan tier (including Free) now ships a real, non-zero
      // storage allowance by default (see PLAN_QUOTA_BYTES) — there's no
      // tier that's blocked out of the box anymore. This only fires for a
      // literal zero-byte cap, which today only happens via an explicit
      // admin override (storageCapBytes = 0), not from plan defaults.
      isHardBlocked: capBytes <= 0,
    };
  }

  /**
   * Throws before anything is saved if the org's storage cap is zero
   * (an explicit override, not a plan default — every plan now includes
   * some storage). Otherwise returns whether this upload would put the org
   * over its cap — allowed, just surfaced as a warning.
   */
  async assertCanUpload(organizationId: string, incomingBytes: number): Promise<{ warning: boolean }> {
    const usage = await this.getUsage(organizationId);
    if (usage.isHardBlocked) {
      throw new ForbiddenException(
        'Image storage is disabled for this account. Contact support to increase your quota.'
      );
    }
    return { warning: usage.usedBytes + incomingBytes > usage.capBytes };
  }
}
